<template>
  <div
    class="cg-chip"
    :class="badgeClasses"
    :style="chipVariables"
    v-on="$listeners"
  >
    <!-- 
      Emitted when the prepend icon is clicked
      @event onPrependIconClick
    -->
    <div
      v-if="showPrependIcon"
      class="cg-chip__prepend-icon"
      @click="$emit('onPrependIconClick')"
    >
      <!-- @slot Adds an item before text. -->
      <slot name="prepend-icon" />
    </div>

    <div class="cg-chip__content">
      <!-- @slot The default Vue slot. -->
      <slot />
    </div>

    <cg-badge v-if="showBadge" v-bind="badge" />

    <!-- 
      Emitted when the append icon is clicked
      @event onAppendIconClick
     -->
    <div
      v-if="showAppendIconSlot"
      class="cg-chip__append-icon"
      @click="$emit('onAppendIconClick')"
    >
      <!-- @slot Adds an item after text and badge. -->
      <slot name="append-icon" />
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { Colors } from '../../utils/Colors';

import type { ChipBadgeOptions } from '../../types'

import CgBadge from '../CgBadge';

type IChipSlots = {
  'prepend-icon'?: unknown;
  'append-icon'?: unknown;
}

export default Vue.extend({
  name: 'CgChip',
  components: {
    CgBadge,
  },
  props: {
    /**
     * Makes the component small.
     */
    small: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Applies specified background color.
     */
    color: {
      type: String as PropType<Colors | String>,
      required: false,
      default: Colors.White,
    },
    /**
     * Applies border to chip.
     */
    bordered: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Removes the ability to click or hover the component.
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Applies specified font color.
     */
    fontColor: {
      type: String as PropType<Colors | String>,
      required: false,
      default: Colors.Grey500,
    },
    /**
     * Applies specified background color on hover.
     */
    hoverColor: {
      type: String as PropType<Colors | String>,
      required: false,
      default: Colors.Teal100,
    },
    /**
     * Adds badge inside the component.
     * @values color?: string | Colors
     * @values fontColor?: string | Colors
     * @values content?: string | number
     */
    badge: {
      type: Object as PropType<ChipBadgeOptions>,
      required: false,
      default: null,
    },
  },
  computed: {
    slots(): IChipSlots {
      return this.$slots;
    },
    showPrependIcon(): boolean {
      return !!this.slots?.['prepend-icon'];
    },
    showAppendIconSlot(): boolean {
      return !!this.slots?.['append-icon'];
    },
    showBadge(): boolean {
      return !!this.badge;
    },
    badgeClasses(): Array<string> {
      const { small, bordered, readonly, showBadge } = this;

      const result: Array<string> = [];

      if (small) { result.push('cg-chip--small'); }
      if (bordered) { result.push('cg-chip--bordered'); }
      if (readonly) { result.push('cg-chip--readonly'); }
      if (showBadge) { result.push('cg-chip--has-badge'); }

      return result;
    },
    chipVariables(): Object {
      const { color, fontColor, hoverColor } = this;

      return {
        '--chip-color': color,
        '--chip-text-color': fontColor,
        '--chip-hover-color': hoverColor,
      };
    }
  },
});
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
